/**
 * Step 4: sign in.
 *
 * Every failure shows the same message. An unknown address and a wrong
 * password are indistinguishable from here, which is the point — the one
 * exception is an account that exists but is not confirmed, where the user
 * genuinely needs to be told to go and look at their email.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { signInInputSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { registerPath, safeReturnPath } from '../../shared/return-path.ts'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { useI18n } from '../i18n/index.tsx'
import { messageKeyFor } from '../lib/api.ts'
import { authClient } from '../lib/auth-client.ts'

export function SignIn() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { refetch: refreshSession } = authClient.useSession()
  // Where the reader was when they chose to sign in; see shared/return-path.ts.
  const [params] = useSearchParams()
  const returnTo = safeReturnPath(params.get('next'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    const parsed = signInInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setError('error.invalid_credentials')
      return
    }

    setBusy(true)
    const { error: failure } = await authClient.signIn.email({
      email: parsed.data.email,
      password: parsed.data.password,
    })
    setBusy(false)

    if (failure) {
      const key = messageKeyFor(failure)
      // Anything that is not "go and confirm your address" or "you are doing
      // this too often" collapses into one message.
      setError(
        key === 'error.email_not_verified' || key === 'error.rate_limited'
          ? key
          : 'error.invalid_credentials',
      )
      return
    }

    // Refresh the session before leaving. The header subscribes to it on every
    // page, so by the time sign-in succeeds the store has already settled on
    // "no session"; without this, /account reads that stale answer and sends
    // the reader straight back here.
    await refreshSession()
    // With no page to return to, the worlds page, as after registering
    // directly (#105, decided by the maintainer on 4 Oct): a reader who signs
    // in arrives somewhere to read, not on their profile.
    await navigate(returnTo ?? '/')
  }

  return (
    <section className="panel">
      <h1>{t('signin.title')}</h1>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          label={t('signin.email.label')}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />

        <Field
          label={t('signin.password.label')}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />

        {error ? <Alert tone="error">{t(error)}</Alert> : null}

        <button type="submit" className="button" disabled={busy}>
          {busy ? t('signin.submitting') : t('signin.submit')}
        </button>
      </form>

      <nav className="panel__links">
        <Link to="/forgot-password">{t('signin.forgot')}</Link>
        {/* A reader sent here from a page, who has no account yet, keeps that page through registration (#105). */}
        <Link to={returnTo === null ? '/register' : registerPath(returnTo)}>{t('signin.no_account')}</Link>
      </nav>
    </section>
  )
}
